import { Result } from "@lib/api/services/result"
import { CheckoutInfo, RawCheckoutResponse } from "@modules/checkout/types/checkout"
import { AvailablePayment, Discount } from "@modules/checkout/types/checkout-state"
import CheckoutRepositories from "@repositories/checkout.repositories"

interface CheckVoucherPayload {
  id: string
  course_name: string
}

export default class CheckoutServices extends CheckoutRepositories {
  async checkVoucher(code: string) {
    try {
      const res = await this.checkRawVoucher(code)

      const { error_code, result } = await this._getResponse<CheckVoucherPayload>(res)

      if (error_code) {
        if (error_code === "ERR_NOT_FOUND") {
          return Result.fail<CheckVoucherPayload>("Kode tidak ditemukan atau sudah kadaluarsa")
        } else {
          return Result.fail<CheckVoucherPayload>()
        }
      }

      return Result.ok<CheckVoucherPayload>(result)
    } catch (error) {
      return Result.fail<CheckVoucherPayload>()
    }
  }

  async redeemVoucher(courseId: string, code: string) {
    try {
      const res = await this.redeemRawVoucher(courseId, code)

      const { error, result } = await this._getResponse<any>(res)

      if (error) {
        return Result.fail<any>()
      }

      return Result.ok<any>(result)
    } catch (e: any) {
      const error = await this._getErrorObject(e)
      let errorMessage: string
      switch (error.error_code) {
        case "used":
          errorMessage = "Kode voucher ini sudah tidak bisa digunakan"
          break
        case "can_not_used":
          errorMessage = "Kode voucher ini sudah kadaluarsa"
          break
        case "exists":
          errorMessage = "Anda sudah membeli kelas ini"
          break
        case "not_exists":
          errorMessage = "Kode voucher tidak ditemukan"
          break
        case "not_match":
          errorMessage = "Kode voucher tidak bisa digunakan di kelas ini"
          break

        default:
          errorMessage = "Terjadi kesalahan, silahkan coba lagi"
          break
      }
      return Result.fail<any>(errorMessage)
    }
  }

  async getAvailablePayment(programId: string) {
    try {
      const res = await this.getRawPaymentMethods(programId)

      const { error, result } = await this._getResponse<AvailablePayment>(res)

      if (error) {
        return Result.fail<AvailablePayment>()
      }

      return Result.ok<AvailablePayment>(result)
    } catch (error: any) {
      return Result.fail<AvailablePayment>()
    }
  }

  async applyPromoCode(programId: string, { code }: { code: string }) {
    try {
      const res = await this.checkRawDiscount(programId, code)
      if (res.ok) {
        const data = await res.json()
        return Result.ok<Discount>(data.result)
      } else {
        return Result.fail<Discount>()
      }
    } catch (e: any) {
      const { message, field } = await this._getErrorObject(e)
      switch (field?.toUpperCase()) {
        case "DISCOUNT_NOT_FOUND_OR_EXPIRED":
          return Result.fail<Discount>(
            "Oops!  Kode diskon tidak ditemukan atau sudah tidak berlaku. Yuk, coba kode lain."
          )
        case "DISCOUNT_NOT_AVAILABLE_FOR_THIS_PROGRAM_INFESTATION":
          return Result.fail<Discount>(
            "Oops! Kode diskon tidak berlaku untuk program ini. Yuk, coba kode lain."
          )
        case "CAN_NOT_BE_APPLIED":
          return Result.fail<Discount>(
            "Oops! Kode diskon tidak bisa digunakan. Yuk, coba kode lain."
          )
        case "DISCOUNT_NOT_AVAILABLE_FOR_THIS_PRODUCT":
        case "DISCOUNT_EXCEPTED_FOR_THIS_PRODUCT":
          return Result.fail<Discount>(
            "Oops! Kode diskon tidak berlaku di produk ini. Yuk, coba kode lain."
          )
        case "OWN_REFERRAL_CODE":
          return Result.fail<Discount>(
            "Oops! Kamu tidak bisa pakai kode referral milikmu. Yuk, coba kode lain."
          )
        case "DISALLOW_USE_DISCOUNT":
          return Result.fail<Discount>(
            "Oops! Saat ini kode diskon tidak tersedia. Yuk, coba kode lain."
          )
        case "DISALLOW_USE_REFERRAL":
          return Result.fail<Discount>(
            "Oops! Saat ini program referral tidak tersedia. Yuk, coba kode lain."
          )
        case "DISCOUNT_CAN_NOT_USED":
        case "CAN_NOT_USED":
          return Result.fail<Discount>(
            "Oops! Kode diskon tidak bisa digunakan. Yuk, coba kode lain. Yuk, coba kode lain."
          )
        default:
          return Result.fail<Discount>(message)
      }
    }
  }

  async submitCheckout(productId: string) {
    try {
      const res = await this.rawCheckout(productId)

      const response = await this._getResponse<RawCheckoutResponse>(res)

      if (response.error) {
        return Result.fail<RawCheckoutResponse>()
      }

      return Result.ok<RawCheckoutResponse>(response.result)
    } catch (e: any) {
      const { error_code } = await this._getErrorObject(e)

      switch (error_code) {
        case "exists": {
          return Result.fail<RawCheckoutResponse>(
            `Kelas ini sudah anda beli. Silahkan cek "Kelas Video Course" untuk mengakses kelas ini`
          )
        }

        case "not_exists": {
          return Result.fail<RawCheckoutResponse>(`Kelas ini tidak tersedia`)
        }

        default: {
          return Result.fail<RawCheckoutResponse>()
        }
      }
    }
  }

  async submitDiscount({ checkoutId, code }: { checkoutId: string; code: string }) {
    try {
      const res = await this.rawSubmitPromo(checkoutId, code)

      const { error } = await this._getResponse<any>(res)

      if (error) {
        return Result.fail<any>()
      }

      return Result.ok<any>({
        message: "Success",
      })
    } catch (error: any) {
      return Result.fail<any>()
    }
  }

  async submitGetInfo({ checkoutId, infoFrom }: { checkoutId: string; infoFrom: string[] }) {
    try {
      const res = await this.rawSubmitGetInfo(checkoutId, infoFrom)

      const { error } = await this._getResponse<any>(res)

      if (error) {
        return Result.fail<any>()
      }

      return Result.ok<any>({
        message: "Success",
      })
    } catch (error: any) {
      return Result.fail<any>()
    }
  }

  async submitPayment(checkoutId: string, paymentMethod: string) {
    try {
      const res = await this.submitRawPayment({
        checkoutId,
        payment_method: paymentMethod,
      })

      const { error, error_code, result } = await this._getResponse<any>(res)

      if (error) {
        let errorText = ""

        if (
          error_code === "payment_method_not_found_or_not_supported" ||
          error_code === "payment_method_not_found"
        ) {
          errorText = "Metode pembayaran tidak ditemukan. Silahkan pilih metode lainnya"
        } else {
          errorText =
            "Terjadi kesalahan saat proses pembayaran. Refresh ulang halaman dan silahkan coba lagi"
        }

        return Result.fail<any>(errorText)
      }

      return Result.ok<any>(result)
    } catch (error: any) {
      console.log(error)
      return Result.fail<any>()
    }
  }

  async getCheckoutDetail(orderNumber: string) {
    try {
      const res = await this.getRawCheckoutDetail(orderNumber)

      const response = await this._getResponse<CheckoutInfo>(res)

      if (response.error) {
        return Result.fail<CheckoutInfo>()
      }

      return Result.ok<CheckoutInfo>(response.result)
    } catch (error: any) {
      return Result.fail<CheckoutInfo>()
    }
  }
}
